import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { deleteWork4MeAgent, getWork4MeAgent, updateWork4MeAgent } from '@/api/agents-api';
import { listProcessSkillOptions } from '@/api/processes-api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { toast } from 'sonner';
import { Work4MeAgentForm } from './Work4MeAgentForm';

export function Work4MeAgentDetail() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<{ id: string }>();
  const parsedAgentId = Number(id);
  const isValidAgentId = Number.isInteger(parsedAgentId) && parsedAgentId > 0;

  const agentQuery = useQuery({
    queryKey: ['work4me-agent', parsedAgentId],
    queryFn: () => getWork4MeAgent(parsedAgentId),
    enabled: isValidAgentId,
  });
  const skillOptionsQuery = useQuery({
    queryKey: ['process-skill-options'],
    queryFn: listProcessSkillOptions,
  });

  const agent = agentQuery.data;
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [instructions, setInstructions] = useState('');
  const [isEnabled, setIsEnabled] = useState(true);
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (!agent) return;
    setName(agent.name);
    setDescription(agent.description ?? '');
    setInstructions(agent.instructions);
    setIsEnabled(agent.isEnabled);
    setSelectedSkillIds(new Set(agent.skillIds));
  }, [agent]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!isValidAgentId) throw new Error('Invalid agent id');
      return updateWork4MeAgent(parsedAgentId, {
        name: name.trim(),
        description: description.trim() ? description.trim() : null,
        instructions: instructions.trim(),
        skillIds: [...selectedSkillIds].sort((a, b) => a - b),
        isEnabled,
      });
    },
    onSuccess: async (updated) => {
      toast.success(`Saved "${updated.name}".`);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['work4me-agent', parsedAgentId] }),
        queryClient.invalidateQueries({ queryKey: ['process-agent-options'] }),
      ]);
    },
    onError: (error: unknown) => {
      const message = error instanceof Error ? error.message : 'Failed to save agent.';
      toast.error(message);
    },
  });

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!isValidAgentId) throw new Error('Invalid agent id');
      return deleteWork4MeAgent(parsedAgentId);
    },
    onSuccess: async () => {
      toast.success('Agent deleted.');
      await queryClient.invalidateQueries({ queryKey: ['process-agent-options'] });
      navigate('/work4me/agents');
    },
    onError: (error: unknown) => {
      toast.error(getAxiosErrorDisplayMessage(error, 'Failed to delete agent.'));
    },
  });

  const initialSkillIds = useMemo(() => new Set(agent?.skillIds ?? []), [agent?.skillIds]);
  const isDirty =
    Boolean(agent)
    && (
      name.trim() !== (agent?.name ?? '')
      || (description.trim() ? description.trim() : '') !== (agent?.description ?? '')
      || instructions.trim() !== (agent?.instructions ?? '')
      || isEnabled !== agent?.isEnabled
      || [...selectedSkillIds].sort((a, b) => a - b).join(',')
        !== [...initialSkillIds].sort((a, b) => a - b).join(',')
    );

  if (!isValidAgentId) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Invalid agent id.</p>
      </div>
    );
  }

  if (agentQuery.isLoading) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-muted-foreground">Loading agent...</p>
      </div>
    );
  }

  if (agentQuery.isError || !agent) {
    return (
      <div className="rounded-lg border bg-card p-5 m-4">
        <p className="text-sm text-destructive">Unable to load agent detail.</p>
      </div>
    );
  }

  return (
    <div className="-mx-4 py-2">
      <div className="px-4 py-3 border-b bg-muted/20">
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-4">
            <h2 className="text-xl font-light tracking-wide">Agent: {agent.name}</h2>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <Badge variant="outline">{agent.ownerScope}</Badge>
              <Badge variant={isEnabled ? 'default' : 'secondary'}>
                {isEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
              <Button type="button" variant="outline" size="sm" onClick={() => navigate('/work4me/agents')}>
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Agents
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                onClick={() => setConfirmDeleteOpen(true)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>

        <Dialog open={confirmDeleteOpen} onOpenChange={setConfirmDeleteOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete agent</DialogTitle>
              <DialogDescription>
                Delete '{agent.name}'? Agents used by a process cannot be deleted — reassign or
                delete those processes first.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setConfirmDeleteOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Work4MeAgentForm
        name={name}
        description={description}
        instructions={instructions}
        isEnabled={isEnabled}
        selectedSkillIds={selectedSkillIds}
        skillOptions={skillOptionsQuery.data?.items ?? []}
        isDirty={isDirty}
        isSaving={saveMutation.isPending}
        onNameChange={setName}
        onDescriptionChange={setDescription}
        onInstructionsChange={setInstructions}
        onIsEnabledChange={setIsEnabled}
        onSkillToggle={(skillId, checked) => {
          setSelectedSkillIds((prev) => {
            const next = new Set(prev);
            if (checked) {
              next.add(skillId);
            } else {
              next.delete(skillId);
            }
            return next;
          });
        }}
        onSave={() => saveMutation.mutate()}
      />
    </div>
  );
}
